import React, {useEffect, useMemo, useRef, useState} from "react";
import {useNavigate} from "react-router-dom";
import {ChevronLeft, ChevronRight, TrendingUp, Clock3, Sparkles, Award, GraduationCap, ArrowRight} from "lucide-react";
import CourseCard from "./CourseCard";
import {LANDING_COURSES, COURSE_CATEGORIES} from "./landingData";
import {useUserPublicCatalog} from "../../store/user";
import {mapPublicCourse} from "./publicCourseMap";

// ── Shared building blocks ──────────────────────────────────────────────────

function SectionHeader({eyebrow, icon: Icon, title, description, action, actionTo, actionVariant = "button"}) {
  const navigate = useNavigate();
  return (
    <div className="mb-7 flex flex-wrap items-end justify-between gap-2">
      <div className="max-w-2xl">
        {eyebrow && (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-blue-700">
            {Icon ? <Icon className="h-3.5 w-3.5" /> : null} {eyebrow}
          </span>
        )}
        <h2 className="mt-3 text-2xl tracking-tight text-slate-900 sm:text-3xl">
          {title}
        </h2>
        {description && <p className="mt-2 text-sm text-slate-600 sm:text-base">{description}</p>}
      </div>
      {action && (
        <button
          type="button"
          onClick={() => navigate(actionTo || "/courses")}
          className={
            actionVariant === "link"
              ? "text-sm font-semibold text-blue-800 underline underline-offset-4 transition-colors hover:text-blue-950"
              : "inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition-all hover:-translate-y-0.5 hover:border-blue-300 hover:text-blue-800"
          }
        >
          {action} {actionVariant === "link" ? null : <ArrowRight className="h-4 w-4" />}
        </button>
      )}
    </div>
  );
}

function CourseGrid({courses, columns = "lg:grid-cols-4"}) {
  return (
    <div className={`grid grid-cols-1 gap-5 sm:grid-cols-2 ${columns}`}>
      {courses.map((course) => (
        <CourseCard key={course.id} course={course} />
      ))}
    </div>
  );
}

function CourseRail({courses, trackClassName = "gap-5", cardClassName = "", dense = false, railWidth}) {
  const ref = useRef(null);
  const scrollBy = (direction) => {
    ref.current?.scrollBy({left: direction * 580, behavior: "smooth"});
  };

  return (
    <div className="relative">
      <button
        type="button"
        aria-label="Scroll left"
        onClick={() => scrollBy(-1)}
        className="absolute -left-2 top-1/2 z-10 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-slate-200 bg-white shadow-md transition-colors hover:bg-slate-50 sm:flex"
      >
        <ChevronLeft className="h-5 w-5 text-slate-600" />
      </button>
      <div
        ref={ref}
        className={`flex overflow-x-auto pb-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden ${trackClassName}`}
      >
        {courses.map((course) => (
          <CourseCard
            key={course.id}
            course={course}
            variant="rail"
            dense={dense}
            railWidth={railWidth}
            className={cardClassName}
          />
        ))}
      </div>
      <button
        type="button"
        aria-label="Scroll right"
        onClick={() => scrollBy(1)}
        className="absolute -right-2 top-1/2 z-10 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-slate-200 bg-white shadow-md transition-colors hover:bg-slate-50 sm:flex"
      >
        <ChevronRight className="h-5 w-5 text-slate-600" />
      </button>
    </div>
  );
}

const Shell = ({children, tone = "white", id}) => (
  <section
    id={id}
    className={`border-t border-slate-100 px-4 py-14 sm:py-16 ${tone === "slate" ? "bg-slate-50" : "bg-white"}`}
  >
    <div className="mx-auto max-w-7xl">{children}</div>
  </section>
);

// ── Sections ────────────────────────────────────────────────────────────────

const byStudents = (a, b) => b.students - a.students;
const byNewest = (a, b) => a.monthsNew - b.monthsNew;

export function PopularCourses() {
  const courses = useMemo(() => [...LANDING_COURSES].sort(byStudents).slice(0, 8), []);
  return (
    <Shell tone="slate" id="popular">
      <SectionHeader
        eyebrow="Popular courses"
        icon={Award}
        title="Most popular with learners"
        description="The courses learners enroll in most — proven content with consistently high ratings."
        action="View all courses"
      />
      <CourseGrid courses={courses} />
    </Shell>
  );
}

export function TrendingNow() {
  const {publicTrendingCourses, publicTrendingCoursesLoading, publicTrendingCoursesError, fetchPublicTrendingCourses} = useUserPublicCatalog();
  useEffect(() => {
    fetchPublicTrendingCourses();
  }, [fetchPublicTrendingCourses]);
  const courses = useMemo(
    () => (publicTrendingCourses || [])
      .map(mapPublicCourse)
      .map((course) => {
        const postedAt = Date.parse(course.uploadedAt);
        const postedWithinWeek = Number.isFinite(postedAt) && postedAt <= Date.now() && Date.now() - postedAt <= 7 * 24 * 60 * 60 * 1000;
        return postedWithinWeek ? {...course, hot: true, badges: {...course.badges, hot: true}} : course;
      })
      .filter((course) => course.bestseller || course.hot),
    [publicTrendingCourses]
  );
  return (
    <Shell tone="white">
      <SectionHeader
        eyebrow="Trending now"
        icon={TrendingUp}
        title="Trending this month"
        description="Bestsellers, hot courses, and courses posted in the last seven days."
        action="View more courses"
        actionTo="/courses"
        actionVariant="link"
      />
      {publicTrendingCoursesLoading ? (
        <div className="flex gap-3 overflow-hidden" aria-label="Loading trending courses">
          {[...Array(4)].map((_, index) => <div key={index} className="h-72 w-[300px] shrink-0 animate-pulse rounded-xl bg-slate-100" />)}
        </div>
      ) : publicTrendingCoursesError ? (
        <p role="status" className="rounded-xl bg-slate-50 px-4 py-5 text-sm text-slate-600">Trending courses couldn’t be loaded right now.</p>
      ) : courses.length ? (
        <CourseRail courses={courses} trackClassName="items-stretch gap-3" cardClassName="self-stretch" dense railWidth="w-[300px]" />
      ) : (
        <p className="rounded-xl bg-slate-50 px-4 py-5 text-sm text-slate-600">No bestselling, hot, or recently posted courses are available right now.</p>
      )}
    </Shell>
  );
}

export function RecommendedCourses() {
  const courses = useMemo(
    () => [...LANDING_COURSES].sort((a, b) => b.reviews - a.reviews).slice(0, 10),
    []
  );
  return (
    <Shell tone="white">
      <SectionHeader
        eyebrow="Recommended for you"
        icon={Sparkles}
        title="Handpicked for curious minds"
        description="A curated mix across technology, business and creative skills to help you go further."
      />
      <CourseRail courses={courses} />
    </Shell>
  );
}

export function NewlyAdded() {
  const courses = useMemo(() => [...LANDING_COURSES].sort(byNewest).slice(0, 4), []);
  return (
    <Shell tone="slate">
      <SectionHeader
        eyebrow="Newly added"
        icon={Clock3}
        title="Recently released courses"
        description="Just published — be among the first to start learning."
        action="Browse catalog"
      />
      <CourseGrid courses={courses} />
    </Shell>
  );
}

export function CoursesBySubject() {
  const subjects = COURSE_CATEGORIES.slice(0, 6);
  const [active, setActive] = useState(subjects[0]);
  const courses = useMemo(
    () => LANDING_COURSES.filter((course) => course.category === active).slice(0, 4),
    [active]
  );

  return (
    <Shell tone="white" id="subjects">


      <div className="mb-6 flex flex-wrap gap-2">
        {subjects.map((subject) => {
          const isActive = subject === active;
          return (
            <button
              key={subject}
              type="button"
              onClick={() => setActive(subject)}
              aria-pressed={isActive}
              className={`rounded-full border px-4 py-2 text-sm font-medium transition-all ${
                isActive
                  ? "border-blue-900 bg-blue-900 text-white"
                  : "border-slate-200 bg-white text-slate-600 hover:border-blue-200 hover:bg-blue-50 hover:text-blue-800"
              }`}
            >
              {subject}
            </button>
          );
        })}
      </div>

      <CourseGrid courses={courses} columns="lg:grid-cols-4" />
    </Shell>
  );
}

export function ContinueExploring() {
  const navigate = useNavigate();
  return (
    <section className="px-4 py-16">
      <div
        className="mx-auto max-w-7xl overflow-hidden rounded-3xl px-6 py-14 text-center sm:px-12"
        style={{background: "linear-gradient(135deg, #0b2a5b 0%, #1e3a8a 55%, #2563eb 100%)"}}
      >
        <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white">
          <GraduationCap className="h-3.5 w-3.5" /> Keep going
        </span>
        <h2 className="mx-auto mt-4 max-w-2xl text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
          Continue exploring the full course catalog
        </h2>
        <p className="mx-auto mt-3 max-w-xl text-sm text-blue-100 sm:text-base">
          Hundreds of courses across every subject. Filter by level, price and rating,
          then add the ones you love straight to your cart.
        </p>
        <div className="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <button
            type="button"
            onClick={() => navigate("/courses")}
            className="inline-flex items-center gap-2 rounded-xl bg-white px-6 py-3 text-sm font-bold text-blue-900 transition-transform hover:-translate-y-0.5"
          >
            Explore all courses <ArrowRight className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => navigate("/register")}
            className="inline-flex items-center gap-2 rounded-xl border border-white/40 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-white/10"
          >
            Create an account
          </button>
        </div>
      </div>
    </section>
  );
}
